import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Coins, CreditCard, Loader2, ShieldCheck } from "lucide-react";
import { api, errMsg, eur } from "@/lib/api";
import { PageShell } from "@/components/Shell";
import { Button } from "@/components/ui/button";

export default function PaymentPage() {
  const { pid } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [w, setW] = useState(null);
  const sessionId = params.get("session_id");
  const started = useRef(false);
  const poll = useCallback(async (tries = 0) => {
    try {
      const { data } = await api.get(`/payments/${pid}/stripe-status`, { params: { session_id: sessionId } });
      setP(data);
      if (data.status === "pagato") { setChecking(false); toast.success("Pagamento confermato"); return; }
      if (tries < 10) setTimeout(() => poll(tries + 1), 2000);
      else { setChecking(false); toast.error("Pagamento non ancora confermato: ricarica la pagina tra poco"); }
    } catch (e) {
      setChecking(false);
      toast.error(errMsg(e));
    }
  }, [pid, sessionId]);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api.get(`/payments/${pid}`).then(({ data }) => setP(data)).catch((e) => toast.error(errMsg(e)));
    api.get("/wallet/me").then(({ data }) => setW(data)).catch(() => {});
    if (params.get("annullato")) toast.info("Pagamento annullato: puoi riprovare quando vuoi");
    if (sessionId) { setChecking(true); poll(); }
    if (params.toString()) window.history.replaceState(null, "", window.location.pathname);
  }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/${pid}/stripe`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  const payWallet = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/${pid}/wallet`);
      setP(data);
      toast.success("Pagato con la Selvix Card");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  if (!p) return null;
  const need = w ? Math.ceil(Math.round(p.amount * 100) * 100 / w.eur_cents_per_slvx) : 0;
  const enough = w && w.card.status === "attiva" && w.wallet.balance_cents >= need;
  return (
    <PageShell title="Pagamento" testid="payment-page">
      <div className="glass max-w-lg space-y-4 rounded-3xl p-6">
        <div className="rounded-xl border-2 border-dashed border-amber-400 bg-amber-50 p-3 text-xs font-semibold text-amber-900" data-testid="payment-test-banner">
          STRIPE IN MODALITÀ DI PROVA – nessun addebito reale. Usa la carta 4242 4242 4242 4242, una data futura e un CVC qualsiasi.
        </div>
        <p className="text-sm text-slate-600">{p.description}</p>
        <p className="font-display text-3xl font-extrabold text-slate-900" data-testid="payment-amount">{eur(p.amount)}</p>
        {p.status === "pagato" ? (
          <div className="space-y-3" data-testid="payment-success">
            <p className="flex items-center gap-2 text-sm font-semibold text-emerald-700"><CheckCircle2 className="h-5 w-5" /> Pagamento registrato{p.provider === "SELVIX_WALLET" ? " con Selvix Card" : ""} · Ricevuta {p.receipt_number}</p>
            <Button data-testid="payment-go-area" onClick={() => navigate("/area-personale")} className="w-full rounded-full bg-sky-600 hover:bg-sky-700">Vai ai miei appuntamenti</Button>
          </div>
        ) : checking ? (
          <p className="flex items-center gap-2 text-sm text-slate-600" data-testid="payment-checking"><Loader2 className="h-4 w-4 animate-spin" /> Verifica del pagamento in corso…</p>
        ) : p.status === "in_attesa" ? (
          <>
            <p className="flex items-center gap-2 text-xs text-slate-500"><ShieldCheck className="h-4 w-4 text-cyan-600" /> Il pagamento avviene sulla pagina sicura di Stripe: i dati della carta non passano da Doctor Selvix.</p>
            <Button data-testid="payment-stripe-btn" disabled={busy} onClick={pay} className="w-full rounded-full bg-sky-600 hover:bg-sky-700">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}Paga con carta (Stripe)</Button>
            {w && (
              <div className="space-y-2 rounded-2xl border border-cyan-200 bg-cyan-50/60 p-3" data-testid="payment-wallet-box">
                <p className="text-xs text-slate-600">Saldo Selvix Card <b data-testid="payment-wallet-balance">{(w.wallet.balance_cents / 100).toLocaleString("it-IT", { minimumFractionDigits: 2 })} SLVX</b> · servono <b>{(need / 100).toLocaleString("it-IT", { minimumFractionDigits: 2 })} SLVX</b></p>
                <Button data-testid="payment-wallet-btn" disabled={busy || !enough} onClick={payWallet} className="w-full rounded-full bg-gradient-to-r from-cyan-600 to-sky-700"><Coins className="mr-2 h-4 w-4" />Paga con Selvix Card</Button>
                {!enough && <p className="text-xs text-amber-700" data-testid="payment-wallet-insufficient">{w.card.status !== "attiva" ? "Tessera sospesa." : "Saldo insufficiente:"} <button className="font-semibold underline" onClick={() => navigate("/area-personale")} data-testid="payment-wallet-topup-link">ricarica la carta</button></p>}
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-600" data-testid="payment-status">Stato del pagamento: {p.status}</p>
        )}
      </div>
    </PageShell>
  );
}
